import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import {
  Download,
  Folder as FolderIcon,
  InsertDriveFile,
  Archive,
} from '@mui/icons-material'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router-dom'
import ReactPaginate from 'react-paginate'
import ResponsiveAppBar from '../ResponsiveAppBar'
import { IStores } from '../../Stores'
import * as FileService from '../../services/FileService'

interface IFileBrowserProps {
  initialPath?: string
}

interface IFileBrowserState {
  files: FileService.FileItem[]
  currentPath: string
  loading: boolean
  currentPage: number
  pageSize: number
  totalPages: number
  totalCount: number
  firstRecordIndex: number
  lastRecordIndex: number
  selectedFiles: Set<string>
  snackbarOpen: boolean
  snackbarMessage: string
  snackbarSeverity: 'success' | 'error'
  isDownloading: boolean
}

@observer
class FileBrowser extends React.Component<IFileBrowserProps, IFileBrowserState> {
  constructor(props: IFileBrowserProps) {
    super(props)
    this.state = {
      files: [],
      currentPath: props.initialPath || '',
      loading: false,
      currentPage: 1,
      pageSize: 25,
      totalPages: 0,
      totalCount: 0,
      firstRecordIndex: 0,
      lastRecordIndex: 0,
      selectedFiles: new Set<string>(),
      snackbarOpen: false,
      snackbarMessage: '',
      snackbarSeverity: 'success',
      isDownloading: false,
    }
  }

  public componentDidMount() {
    this.fetchFiles(this.state.currentPath)
  }

  private fetchFiles = async (path: string, page: number = 1) => {
    this.setState({ loading: true })
    try {
      console.log('Fetching files with path:', {
        path,
        page,
        pageSize: this.state.pageSize,
      })
      const response = await FileService.listFiles(path, page, this.state.pageSize)
      const totalCount = response.totalCount || 0
      console.log('Received response:', { itemsCount: response.items.length, totalCount })
      this.setState(
        {
          files: response.items,
          currentPath: path,
          currentPage: response.pageNumber,
          totalPages: response.totalPages,
          totalCount: totalCount,
        },
        () => {
          if (totalCount > 0) {
            this.setPaginationOffsetData()
          }
        }
      )
    } catch (error: any) {
      console.error('Error loading files:', error)
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to load files'
      this.setState({
        snackbarOpen: true,
        snackbarMessage: errorMessage,
        snackbarSeverity: 'error',
      })
    } finally {
      this.setState({ loading: false, isDownloading: false })
    }
  }

  private setPaginationOffsetData = () => {
    const { currentPage, totalCount, pageSize } = this.state
    const firstRecordIndex = (currentPage - 1) * pageSize + 1
    const lastRecordIndex =
      currentPage * pageSize < totalCount ? currentPage * pageSize : totalCount
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
    })
  }

  private navigateToFolder = (path: string) => {
    const relativePath = path.replace(/^\/+|\/+$/g, '')

    this.setState({ currentPage: 1, selectedFiles: new Set<string>() }, () => {
      this.fetchFiles(relativePath, 1)
    })
  }

  private toggleFileSelection = (filePath: string) => {
    const newSelected = new Set(this.state.selectedFiles)
    if (newSelected.has(filePath)) {
      newSelected.delete(filePath)
    } else {
      newSelected.add(filePath)
    }
    this.setState({ selectedFiles: newSelected })
  }

  private toggleSelectAll = () => {
    const selectableFiles = this.state.files.filter((f) => !f.isDirectory)
    const allFilesPaths = selectableFiles.map((f) => f.path)

    const allSelected =
      selectableFiles.length > 0 &&
      allFilesPaths.every((path) => this.state.selectedFiles.has(path))

    if (allSelected) {
      this.setState({ selectedFiles: new Set<string>() })
    } else {
      this.setState({ selectedFiles: new Set(allFilesPaths) })
    }
  }

  private handleDownloadSelected = async () => {
    const selectedFiles = this.state.files.filter((f) =>
      this.state.selectedFiles.has(f.path)
    )
    if (selectedFiles.length === 0) {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: 'Please select files to download',
        snackbarSeverity: 'error',
      })
      return
    }

    this.setState({ isDownloading: true })

    let successCount = 0
    let failCount = 0

    for (const file of selectedFiles) {
      try {
        await this.handleDownload(file, false)
        successCount++
        await new Promise((resolve) => setTimeout(resolve, 500))
      } catch (error) {
        console.error(`Error downloading ${file.name}:`, error)
        failCount++
      }
    }

    if (failCount > 0) {
      this.setState({
        isDownloading: false,
        snackbarOpen: true,
        snackbarMessage: `${successCount} file(s) downloaded, ${failCount} failed`,
        snackbarSeverity: 'error',
      })
    } else {
      this.setState({
        isDownloading: false,
        selectedFiles: new Set<string>(),
        snackbarOpen: true,
        snackbarMessage: `${successCount} file(s) downloaded successfully`,
        snackbarSeverity: 'success',
      })
    }
  }

  private handleDownloadAsZip = async () => {
    const selectedFiles = this.state.files.filter((f) =>
      this.state.selectedFiles.has(f.path)
    )
    if (selectedFiles.length === 0) {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: 'Please select files to download',
        snackbarSeverity: 'error',
      })
      return
    }

    this.setState({ isDownloading: true })

    try {
      const filePaths = selectedFiles.map((f) => f.path)
      const blob = await FileService.downloadFilesAsZip(filePaths)

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
      const zipFileName = `files-${timestamp}.zip`

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = zipFileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      this.setState({
        isDownloading: false,
        selectedFiles: new Set<string>(),
        snackbarOpen: true,
        snackbarMessage: 'ZIP file downloaded successfully',
        snackbarSeverity: 'success',
      })
    } catch (error: any) {
      console.error('Error downloading ZIP:', error)
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to download ZIP file'
      this.setState({
        isDownloading: false,
        snackbarOpen: true,
        snackbarMessage: errorMessage,
        snackbarSeverity: 'error',
      })
    }
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ currentPage: page + 1 }, () => {
      this.fetchFiles(this.state.currentPath, page + 1)
    })
  }

  private paginationInfo = () => {
    if (this.state.totalCount > 0) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalCount} entries
          </Typography>
        </div>
      )
    }
    return null
  }

  private handleDownload = async (
    file: FileService.FileItem,
    showNotification = true
  ) => {
    if (showNotification) {
      this.setState({ isDownloading: true })
    }

    try {
      const blob = await FileService.downloadFile(file.path)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = file.name
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      if (showNotification) {
        this.setState({
          isDownloading: false,
          snackbarOpen: true,
          snackbarMessage: 'File downloaded successfully',
          snackbarSeverity: 'success',
        })
      }
    } catch (error: any) {
      console.error('Error downloading file:', error)
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        `Failed to download ${file.name}`
      if (showNotification) {
        this.setState({
          isDownloading: false,
          snackbarOpen: true,
          snackbarMessage: errorMessage,
          snackbarSeverity: 'error',
        })
      }
      throw error
    }
  }

  private getBreadcrumbs = () => {
    const { currentPath } = this.state
    if (!currentPath) return [{ name: 'PatientDocuments_Ascent', path: '' }]

    const parts = currentPath.split('/').filter((p) => p)
    const breadcrumbs: Array<{ name: string; path: string }> = [
      { name: 'PatientDocuments_Ascent', path: '' },
    ]

    parts.forEach((part, index) => {
      const path = parts.slice(0, index + 1).join('/')
      breadcrumbs.push({ name: part, path })
    })

    return breadcrumbs
  }

  private getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase()
    const icons: Record<string, string> = {
      pdf: '📕',
      doc: '📘',
      docx: '📘',
      xls: '📗',
      xlsx: '📗',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      zip: '🗜️',
      rar: '🗜️',
      txt: '📝',
      mp4: '🎬',
      avi: '🎬',
      mp3: '🎵',
      wav: '🎵',
    }
    return icons[ext || ''] || '📄'
  }

  private formatFileSize = (bytes?: number) => {
    if (!bytes) return ''
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 Bytes'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i]
  }

  private formatDate = (dateString?: string) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleString()
  }

  public render() {
    const { files, loading, selectedFiles } = this.state
    const selectedCount = selectedFiles.size
    const selectableFiles = files.filter((f) => !f.isDirectory)
    const allFilesSelected =
      selectableFiles.length > 0 && selectedCount === selectableFiles.length

    return (
      <>
        <Helmet>
          <title>File Browser</title>
        </Helmet>
        <ResponsiveAppBar title="File Browser" pageIcon={<FolderIcon />} />
        <Box
          sx={{
            height: 'calc(100vh - 64px)',
            overflow: 'auto',
            bgcolor: 'background.default',
            p: 2,
          }}
        >
          <Paper elevation={3} sx={{ minHeight: 'calc(100% - 32px)', p: 3 }}>
            <Stack spacing={3}>
              {/* Breadcrumb Navigation */}
              <Breadcrumbs>
                {this.getBreadcrumbs().map((crumb) => (
                  <Link
                    key={crumb.path}
                    component="button"
                    variant="body2"
                    onClick={() => this.navigateToFolder(crumb.path)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {crumb.name}
                  </Link>
                ))}
              </Breadcrumbs>

              {/* Action Bar */}
              <Box
                display="flex"
                gap={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" gap={1} alignItems="center">
                  {selectableFiles.length > 0 && (
                    <Checkbox
                      checked={allFilesSelected}
                      indeterminate={selectedCount > 0 && !allFilesSelected}
                      onChange={this.toggleSelectAll}
                      title="Select All"
                    />
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {selectedCount > 0
                      ? `${selectedCount} file(s) selected`
                      : 'Select files to download'}
                  </Typography>
                </Box>
                <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<Archive />}
                      onClick={this.handleDownloadAsZip}
                      disabled={this.state.isDownloading || selectedCount === 0}
                    >
                      Download as ZIP ({selectedCount})
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Download />}
                      onClick={this.handleDownloadSelected}
                      disabled={this.state.isDownloading || selectedCount === 0}
                    >
                      Download Selected ({selectedCount})
                    </Button>
                  </>
                </Box>
              </Box>

              <Box sx={{ minHeight: 400 }}>
                {loading ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </Box>
                ) : files.length === 0 ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <Typography variant="body2" color="text.secondary">
                      No files or folders
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {files.map((file) => (
                      <ListItem
                        key={file.path}
                        secondaryAction={
                          !file.isDirectory && (
                            <IconButton
                              edge="end"
                              onClick={() => this.handleDownload(file)}
                              title="Download"
                              disabled={this.state.isDownloading}
                            >
                              <Download />
                            </IconButton>
                          )
                        }
                        disablePadding
                      >
                        {!file.isDirectory && (
                          <Checkbox
                            checked={selectedFiles.has(file.path)}
                            onChange={() => {
                              this.toggleFileSelection(file.path)
                            }}
                            sx={{ ml: 1 }}
                          />
                        )}
                        <ListItemButton
                          onClick={() => {
                            if (file.isDirectory) {
                              this.navigateToFolder(file.path)
                            } else {
                              this.toggleFileSelection(file.path)
                            }
                          }}
                          sx={{ pl: file.isDirectory ? 2 : 0 }}
                        >
                          <ListItemIcon>
                            {file.isDirectory ? (
                              <FolderIcon color="primary" />
                            ) : (
                              <InsertDriveFile />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography>
                                {file.isDirectory ? '' : this.getFileIcon(file.name)}{' '}
                                {file.name}
                              </Typography>
                            }
                            secondary={
                              !file.isDirectory && (
                                <Typography variant="caption" color="text.secondary">
                                  {this.formatFileSize(file.size)} •{' '}
                                  {this.formatDate(file.lastModified)}
                                </Typography>
                              )
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>

              {/* Pagination Controls */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <div className="pagination-row">
                  {this.paginationInfo()}
                  <ReactPaginate
                    forcePage={this.state.currentPage - 1}
                    previousLabel={'<'}
                    nextLabel={'>'}
                    onPageChange={this.handlePageChange}
                    pageCount={this.state.totalPages}
                    containerClassName={'pagination'}
                    activeClassName={'active'}
                    //@ts-ignore
                    renderOnZeroPageCount={null}
                  />
                </div>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {this.state.isDownloading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999,
            }}
          >
            <CircularProgress size={60} />
          </Box>
        )}

        <Snackbar
          open={this.state.snackbarOpen}
          autoHideDuration={4000}
          onClose={() => this.setState({ snackbarOpen: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => this.setState({ snackbarOpen: false })}
            severity={this.state.snackbarSeverity}
            sx={{
              width: '100%',
              ...(this.state.snackbarSeverity === 'success' && {
                backgroundColor: '#43a047',
                color: 'white',
                '& .MuiAlert-icon': {
                  color: 'white',
                },
              }),
              ...(this.state.snackbarSeverity === 'error' && {
                backgroundColor: '#d32f2f',
                color: 'white',
                '& .MuiAlert-icon': {
                  color: 'white',
                },
              }),
            }}
          >
            {this.state.snackbarMessage}
          </Alert>
        </Snackbar>
      </>
    )
  }
}

const InjectedFileBrowser = inject<
  IStores,
  IFileBrowserProps,
  Partial<IFileBrowserProps>,
  any
>(() => ({}))(FileBrowser)

const FileBrowserWithRouter: React.FC = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  let initialPath = queryParams.get('path') || ''

  if (initialPath) {
    initialPath = initialPath.replace(/^\/+|\/+$/g, '')
  }

  return <InjectedFileBrowser initialPath={initialPath} />
}

export default FileBrowserWithRouter
